import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Building2, Send, Users, CheckCircle2 } from "lucide-react";

const FEATURES = [
  { id: "qr", label: "QR + Geolocalizzazione" },
  { id: "signature", label: "Firma digitale" },
  { id: "payroll", label: "Export gestionale paghe" },
  { id: "expenses", label: "Rimborsi spese" },
  { id: "shifts", label: "Turni & Timesheet" },
  { id: "ai", label: "AI Analisi presenze" },
  { id: "sso", label: "SSO / Active Directory" },
  { id: "api", label: "API personalizzate" },
];

export function EnterpriseQuoteModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    full_name: "", role: "", email: "", phone: "",
    company_name: "", employees_count: "", sectors: "",
    multi_site: false, needed_features: [], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleFeature = (id) => setForm((f) => ({
    ...f,
    needed_features: f.needed_features.includes(id)
      ? f.needed_features.filter((x) => x !== id)
      : [...f.needed_features, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.company_name || !form.employees_count) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/enterprise-quote", {
        ...form,
        employees_count: parseInt(form.employees_count, 10) || 0,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio richiesta");
    } finally { setSubmitting(false); }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setDone(false);
      setForm({ full_name: "", role: "", email: "", phone: "", company_name: "", employees_count: "", sectors: "", multi_site: false, needed_features: [], message: "" });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => o ? onOpenChange(true) : close()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="enterprise-quote-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#3B82F6]"/>
            {done ? "Richiesta inviata" : "Richiedi preventivo personalizzato"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#757575]">
            {done ? "Ti ricontatteremo entro 24 ore lavorative." : "Compila il form: un consulente HR ti contatterà entro 24 ore lavorative."}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="text-center py-6" data-testid="quote-success">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600"/>
            </div>
            <p className="text-base text-[#0A0A0A] font-semibold mb-1">Grazie per averci contattato!</p>
            <p className="text-sm text-[#555] mb-6">Un consulente HR ti chiamerà entro 24 ore lavorative al numero indicato per definire la soluzione su misura per la tua azienda.</p>
            <Button onClick={close} className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">Chiudi</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="bg-[#0F1428] border border-[#1F2540] rounded-xl p-3 text-xs text-[#555] flex items-start gap-2">
              <Users className="w-4 h-4 text-[#3B82F6] flex-shrink-0 mt-0.5"/>
              <span>Per aziende con <strong>più di 50 dipendenti</strong> creiamo un piano su misura: pricing dedicato, SSO, integrazione paghe avanzata e supporto prioritario.</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome e cognome *</Label>
                <Input data-testid="quote-fullname" required value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})} className="rounded-xl mt-1" placeholder="Mario Rossi"/>
              </div>
              <div>
                <Label>Ruolo</Label>
                <Input value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} className="rounded-xl mt-1" placeholder="HR Manager"/>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email *</Label>
                <Input data-testid="quote-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="rounded-xl mt-1" placeholder="mario@azienda.it"/>
              </div>
              <div>
                <Label>Telefono *</Label>
                <Input data-testid="quote-phone" required value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="rounded-xl mt-1" placeholder="+39 333 1234567"/>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Azienda *</Label>
                <Input data-testid="quote-company" required value={form.company_name} onChange={(e)=>setForm({...form, company_name: e.target.value})} className="rounded-xl mt-1" placeholder="Rossi & Partners SRL"/>
              </div>
              <div>
                <Label>Dipendenti *</Label>
                <Input data-testid="quote-employees" required type="number" min="1" value={form.employees_count} onChange={(e)=>setForm({...form, employees_count: e.target.value})} className="rounded-xl mt-1" placeholder="120"/>
              </div>
            </div>

            <div>
              <Label>Settore</Label>
              <Input value={form.sectors} onChange={(e)=>setForm({...form, sectors: e.target.value})} className="rounded-xl mt-1" placeholder="Edilizia, Manifattura, Servizi…"/>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.multi_site} onChange={(e)=>setForm({...form, multi_site: e.target.checked})} className="w-4 h-4 accent-[#3B82F6]"/>
              <span>Azienda multi-sede</span>
            </label>

            <div>
              <Label className="mb-2 block">Funzioni che ti interessano</Label>
              <div className="flex flex-wrap gap-2">
                {FEATURES.map((f) => {
                  const active = form.needed_features.includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleFeature(f.id)}
                      data-testid={`quote-feature-${f.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-[#0A0A0A] border-[#1F2540] hover:border-[#3B82F6]"}`}
                    >
                      {active ? "✓ " : "+ "}{f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Note aggiuntive</Label>
              <Textarea value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} className="rounded-xl mt-1 min-h-20" placeholder="Esigenze specifiche, integrazioni richieste, tempistiche…"/>
            </div>

            <Button type="submit" disabled={submitting} data-testid="quote-submit" className="w-full rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] btn-glow h-12">
              <Send className="w-4 h-4 mr-2"/>
              {submitting ? "Invio in corso…" : "Invia richiesta preventivo"}
            </Button>
            <p className="text-[10px] text-[#999] text-center">I tuoi dati sono protetti e non verranno mai condivisi con terze parti.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
